import {
  useState,
  createContext,
  useMemo,
  useContext,
  useEffect,
  Dispatch,
  SetStateAction,
  memo,
  useRef,
  useCallback,
} from 'react';
import { useDataProvider } from 'react-admin';
import { BloxCustomDataProviderType } from 'utility/types/dataProvider';
import { SavedSearchItem } from 'providers/contexts/extensions/SavedSearches';
import SkeletonBox from 'components/generic/SkeletonBox';
import { useQuery } from '@tanstack/react-query';

// ---------- INTERFACES / TYPES / CONSTS ---------- //

type StateDSS = {
  initialized: boolean; // have we initialized the state
  hasDefaultSavedSearch: boolean; // does user have a default saved search
  defaultSavedSearch: SavedSearchItem | null; // the saved search data or null if unset
};

interface SavedSearchContextType {
  savedSearchOpen: boolean;
  setSavedSearchOpen: Dispatch<SetStateAction<boolean>>;
  searchRefId: string;
  setSearchRefId: Dispatch<SetStateAction<string>>;
  defaultSavedSearchState: StateDSS;
  setDefaultSavedSearchState: Dispatch<SetStateAction<StateDSS>>;
  refetch: () => void;
  // Saved Search / Quick Filters
  quickFilterValue: SavedSearchItem | null; // quick filter dropdown select value
  setQuickFilterValue: Dispatch<SetStateAction<SavedSearchItem | null>>; // raw state setter if needed
  updateQuickFilterValue: (item: SavedSearchItem | null) => void; // update quick filter state + last ref together
  lastQuickFilterRef: React.MutableRefObject<SavedSearchItem | null>; // tracks last applied quick filter value
}

// ---------- SEARCH CONTEXT PROVIDER  ---------- //
export const SavedSearchContext = createContext<SavedSearchContextType | undefined>(
  undefined,
);

export const useSavedSearch = () => {
  const context = useContext(SavedSearchContext);
  if (!context) {
    throw new Error('useSavedSearch must be used within a SavedSearchContextProvider');
  }
  return context;
};

/**
 * Helper to check if user has a default saved search
 * used in SearchContextProvider to determine if the
 * default saved search should be applied or not.
 * Note: may not be needed anymore.
 */

export function checkHasDefaultSavedSearch(dssState: StateDSS): boolean {
  // null/undefined or not an object at all (eg state uninitialized)
  if (!dssState || typeof dssState !== 'object') {
    return false;
  }
  // state not initialized yet
  if (dssState.initialized === false) {
    return false;
  }
  // no default saved search is set
  if (!dssState?.defaultSavedSearch) {
    return false;
  }
  // check empty object as maybe api 404 so got empty obj back...
  if (Object.keys(dssState?.defaultSavedSearch).length === 0) {
    return false;
  }
  // ensure payload key truthy in object
  if (!dssState?.defaultSavedSearch?.payload) {
    return false;
  }
  // here user should actually have a default saved search
  return true;
}

type DSSInfo = {
  enabled: boolean;
  target: string;
};

/**
 * Helper for determing if query will be
 * enabled or not based on the resource (app)
 * returns { enabled: boolean, app: string }
 */
export function getDefaultSavedSearchInfo(resource?: string): DSSInfo {
  switch (resource) {
    case 'editorial': {
      return {
        enabled: true,
        target: 'editorial',
      };
    }
    case 'wire': {
      return {
        enabled: true,
        target: 'wire',
      };
    }
    default: {
      return {
        enabled: false,
        target: '',
      };
    }
  }
}

type SavedSearchContextProviderProps = {
  children?: React.ReactNode;
  app?: string;
};

const initState: StateDSS = {
  initialized: false,
  hasDefaultSavedSearch: false,
  defaultSavedSearch: null,
};

export const SavedSearchContextProvider = memo(
  ({ children, app }: SavedSearchContextProviderProps) => {
    const dataProvider = useDataProvider<BloxCustomDataProviderType>();
    const [savedSearchOpen, setSavedSearchOpen] = useState(false);
    const [searchRefId, setSearchRefId] = useState<string>('');

    // default saved search state
    const [defaultSavedSearchState, setDefaultSavedSearchState] =
      useState<StateDSS>(initState);

    // does this app have default saved search support?
    const { target, enabled } = getDefaultSavedSearchInfo(app);

    // quick filter dropdown state/ref/helpers
    const [quickFilterValue, setQuickFilterValue] = useState<SavedSearchItem | null>(
      null,
    );
    const lastQuickFilterRef = useRef<SavedSearchItem | null>(null);

    // update quick filter state and ref together
    const updateQuickFilterValue = useCallback(
      (item: SavedSearchItem | null) => {
        setQuickFilterValue(item);
        lastQuickFilterRef.current = item;
      },
      [setQuickFilterValue],
    );

    // endpoint considers 404 as a valid response (no saved search)
    // SavedSearches.tsx extension for more details
    const {
      data: defaultSavedSearchResponse,
      status: defaultSavedSearchStatus,
      refetch: refetchDefaultSavedSearch,
    } = useQuery({
      queryKey: ['getDefaultSavedSearch', target],
      queryFn: async () => {
        const response = await dataProvider.getDefaultSavedSearch(target);
        return response;
      },
      enabled: enabled,
      staleTime: 0,
      gcTime: 0,
      placeholderData: (previousData) => previousData,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    });

    // If disabled set to false to unblock render
    // since no fetch will occur, and state must be initialized
    useEffect(() => {
      if (!enabled) {
        setDefaultSavedSearchState((prevState) => ({
          ...prevState,
          initialized: true,
          hasDefaultSavedSearch: false,
          defaultSavedSearch: null,
        }));
      }
    }, [enabled]);

    // Setup the saved search response data to state
    useEffect(() => {
      // above effect will set state to false
      // and return early if not enabled
      if (!enabled) {
        return;
      }
      // status 204 means user has no saved search
      // unforutnatley means cant easily differentiate
      // between a real api errors or no error...
      if (
        defaultSavedSearchResponse?.status === 204 ||
        /**
         * Checking for 404 in case backend is still using 404 for no saved
         * search instead of 204; otherwise some search pages will fail to load
         * and go into infinite loop trying to fetch data
         */
        defaultSavedSearchResponse?.status === 404 ||
        defaultSavedSearchStatus === 'error'
      ) {
        setDefaultSavedSearchState((prevState) => ({
          ...prevState,
          initialized: true,
          hasDefaultSavedSearch: false,
          defaultSavedSearch: null,
        }));
        // note: this return is important due to the 204
        // status workarounds we have setup, as if we dont
        // return here, we may fallthrough to the next check.
        return;
      }

      // Saved search data has loaded
      if (defaultSavedSearchResponse?.data) {
        const dssData = defaultSavedSearchResponse?.data || {};
        setDefaultSavedSearchState((prevState) => ({
          ...prevState,
          initialized: true,
          hasDefaultSavedSearch: true,
          defaultSavedSearch: dssData,
        }));
        return;
      }
    }, [
      defaultSavedSearchResponse,
      defaultSavedSearchResponse?.data,
      defaultSavedSearchResponse?.status,
      defaultSavedSearchStatus,
      enabled,
    ]);

    const contextValue = useMemo(
      () => ({
        savedSearchOpen,
        setSavedSearchOpen,
        searchRefId,
        setSearchRefId,
        defaultSavedSearchState,
        setDefaultSavedSearchState,
        refetch: refetchDefaultSavedSearch,
        setQuickFilterValue,
        quickFilterValue,
        updateQuickFilterValue,
        lastQuickFilterRef,
      }),
      [
        savedSearchOpen,
        setSavedSearchOpen,
        searchRefId,
        setSearchRefId,
        defaultSavedSearchState,
        setDefaultSavedSearchState,
        refetchDefaultSavedSearch,
        setQuickFilterValue,
        quickFilterValue,
        updateQuickFilterValue,
        lastQuickFilterRef,
      ],
    );

    // wait until the DSS fetch attempt finishes
    // and we consider the state initialized
    if (!defaultSavedSearchState.initialized) {
      return (
        <SkeletonBox
          width="100%"
          height="100%"
          text="Loading..."
        />
      );
    }

    return (
      <SavedSearchContext.Provider value={contextValue}>
        {children}
      </SavedSearchContext.Provider>
    );
  },
);

SavedSearchContextProvider.displayName = 'SavedSearchContextProvider';
